import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as d3 from 'd3';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  TagIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

interface SimNode extends d3.SimulationNodeDatum {
  id: string;
  title: string;
  type: 'main' | 'branch';
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  source: SimNode | string;
  target: SimNode | string;
}

// Helper function to render text with bold formatting
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={index} style={{ fontWeight: 600, color: 'rgba(255,255,255,0.95)' }}>{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

interface BranchingAnimationFullProps {
  scale?: number;
  isMobile?: boolean;
}

export const BranchingAnimationFull: React.FC<BranchingAnimationFullProps> = ({ scale = 1, isMobile = false }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<SimNode, SimLink> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRunningRef = useRef(false);

  // Animation states
  const [animationPhase, setAnimationPhase] = useState<'init' | 'selecting' | 'showButton' | 'clicking' | 'branching' | 'movingToNode' | 'clickingNode' | 'showingNewPopup' | 'typing' | 'sending' | 'streaming' | 'reset'>('init');
  const [cursorPosition, setCursorPosition] = useState({ x: 180, y: 220 });
  const [textSelected, setTextSelected] = useState(false);
  const [showRamifyButton, setShowRamifyButton] = useState(false);
  const [newNodeData, setNewNodeData] = useState<SimNode | null>(null);
  const [showSecondPopup, setShowSecondPopup] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [streamedText, setStreamedText] = useState('');

  // Initial nodes
  const [nodes, setNodes] = useState<SimNode[]>([
    { id: 'root', title: 'Climate Change', type: 'main' },
    { id: 'branch1', title: 'Renewable Energy', type: 'branch' },
    { id: 'branch2', title: 'Carbon Capture', type: 'branch' },
  ]);

  const [links, setLinks] = useState<SimLink[]>([
    { source: 'root', target: 'branch1' },
    { source: 'root', target: 'branch2' },
  ]);

  const userMessage = "Explain climate change solutions";
  const aiResponse = "Climate change solutions fall into two main categories:\n\n**Mitigation** - Reducing greenhouse gas emissions through renewable energy.\n\n**Adaptation** - Adjusting to climate impacts through infrastructure improvements.";

  // Initialize D3 graph
  useEffect(() => {
    if (!svgRef.current) return;

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);

    // Add arrow marker definition
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 18)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .append('svg:path')
      .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
      .attr('fill', '#5a5a5a')
      .style('stroke', 'none');

    const graphGroup = svg.append('g').attr('class', 'graph-group');

    // Create force simulation
    const simulation = d3.forceSimulation<SimNode>(nodes)
      .force('link', d3.forceLink<SimNode, SimLink>(links).id((d: any) => d.id).distance(120 * scale))
      .force('charge', d3.forceManyBody<SimNode>().strength(-400 * scale))
      .force('center', d3.forceCenter(width * 0.4, isMobile ? height / 2 : height / 2.5))  // Center in available space
      .force('collision', d3.forceCollide<SimNode>().radius(35 * scale));

    simulationRef.current = simulation;

    // Create links
    const linkSelection = graphGroup.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#5a5a5a')
      .attr('stroke-width', 2 * scale)
      .attr('marker-end', 'url(#arrowhead)');

    // Create node groups
    const nodeSelection = graphGroup.append('g')
      .selectAll('g')
      .data(nodes)
      .join('g');

    // Add circles to nodes
    nodeSelection.append('circle')
      .attr('r', 20 * scale)  // All nodes same size as central node
      .attr('fill', (d) => d.type === 'main' ? '#94d2bd' : '#6b7280')  // Light gray for branch nodes
      .attr('stroke', (d) => d.type === 'main' ? '#ffffff' : '#94d2bd')
      .attr('stroke-width', 0);

    // Add labels
    nodeSelection.append('text')
      .attr('text-anchor', (d) => d.type === 'main' ? 'end' : 'start')  // Right-align for main node, left-align for branches
      .attr('dx', (d) => d.type === 'main' ? `-${25 * scale}` : `${25 * scale}`)  // Position to the left for main, right for branches
      .attr('dy', '0.35em')  // Vertically center with the node
      .attr('fill', 'white')
      .attr('font-size', `${11 * scale}px`)
      .attr('font-weight', '500')
      .text((d) => d.title);  // Show full title

    // Update positions on tick
    simulation.on('tick', () => {
      linkSelection
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeSelection.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
    };
  }, [nodes, links, isMobile]);

  // Animation sequence
  useEffect(() => {
    const runSequence = async () => {
      if (animationRunningRef.current) return;
      animationRunningRef.current = true;

      // Wait before starting
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Phase 1: Move cursor to text
      setAnimationPhase('selecting');
      setCursorPosition({ x: 180, y: 220 });
      await new Promise(resolve => setTimeout(resolve, 800));

      // Phase 2: Select text
      setTextSelected(true);
      await new Promise(resolve => setTimeout(resolve, 600));

      // Phase 3: Show Ramify button
      setAnimationPhase('showButton');
      setShowRamifyButton(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Phase 4: Move cursor to button
      setCursorPosition({ x: 180, y: 260 });
      await new Promise(resolve => setTimeout(resolve, 600));

      // Phase 5: Click animation
      setAnimationPhase('clicking');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Phase 6: Hide button (keep text selected)
      setShowRamifyButton(false);

      // Phase 7: Add new node
      setAnimationPhase('branching');

      // Pin existing nodes in place and add new node starting at parent position
      setNodes(prev => {
        // Fix all existing nodes in place
        const fixedNodes = prev.map(node => ({
          ...node,
          fx: node.x,
          fy: node.y
        }));

        const parentNode = fixedNodes.find(n => n.id === 'root');
        const linkDistance = 120; // Same as simulation link distance
        const parentX = parentNode?.x || 0;
        const parentY = parentNode?.y || 0;

        const newNode: SimNode = {
          id: 'branch-mitigation',
          title: 'Mitigation Strategies',
          type: 'branch',
          x: parentX,
          y: parentY,  // Start just below parent
          //fx: parentX,  // Fix position to prevent forces from pulling
          //fy: parentY + 20
        };

        setNewNodeData(newNode);

        return [...fixedNodes, newNode];
      });
      setLinks(prev => [...prev, { source: 'root', target: 'branch-mitigation' }]);

      // Don't add the link yet - wait until after the animation completes
      // This prevents the D3 link force from pulling the node during emergence

      // Manually animate the new node to the correct distance
      if (simulationRef.current) {
        const parentNode = nodes.find(n => n.id === 'root');
        const parentX = parentNode?.x || 0;
        const targetX = parentX + 120;
        const startX = parentX + 20;
        const duration = 1000; // 1 second animation
        const startTime = Date.now();

        const animateNode = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Ease out cubic for smooth deceleration
          const eased = 1 - Math.pow(1 - progress, 3);
          const currentX = startX + (targetX - startX) * eased;

          // Update the node's fixed position
          const newNodeInSim = simulationRef.current?.nodes().find((n: SimNode) => n.id === 'branch-mitigation');
          if (newNodeInSim) {
            newNodeInSim.fx = currentX;
            newNodeInSim.x = currentX;
          }

          //simulationRef.current?.alpha(0.01).restart();

          if (progress < 1) {
            requestAnimationFrame(animateNode);
          } else {
            // Release the node and add the link after animation completes
            //if (newNodeInSim) {
            //  newNodeInSim.fx = null;
            //  newNodeInSim.fy = null;
            //}
          }
        };

        animateNode();
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      // Phase 8: Move cursor to new node
      setAnimationPhase('movingToNode');
      // Get the new node position from the D3 simulation
      if (simulationRef.current) {
        const simNodes = simulationRef.current.nodes();
        const newNode = simNodes.find((n: SimNode) => n.id === 'branch-mitigation');
        if (newNode) {
          const newNodeX = newNode.x || 0;
          const newNodeY = newNode.y || 0;
          setCursorPosition({ x: newNodeX, y: newNodeY });
        }
      }
      await new Promise(resolve => setTimeout(resolve, 800));

      // Phase 9: Click on new node
      setAnimationPhase('clickingNode');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Phase 10: Show second popup
      setAnimationPhase('showingNewPopup');
      setShowSecondPopup(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      // Phase 11: Simulate typing
      setAnimationPhase('typing');
      const messageToType = 'Tell me more about mitigation';
      for (let i = 0; i <= messageToType.length; i++) {
        setTypedText(messageToType.slice(0, i));
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      await new Promise(resolve => setTimeout(resolve, 400));

      // Phase 12: Send message
      setAnimationPhase('sending');
      setShowUserMessage(true);
      setTypedText('');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Phase 13: Stream AI response
      setAnimationPhase('streaming');
      const aiMessage = 'Climate change mitigation involves reducing greenhouse gas emissions through: Renewable Energy: Transitioning to solar, wind, and hydroelectric power. Energy Efficiency: Improving building insulation and transportation systems.';
      const words = aiMessage.split(' ');
      let currentText = '';
      for (let i = 0; i < words.length; i++) {
        currentText += (i === 0 ? '' : ' ') + words[i];
        setStreamedText(currentText);
        await new Promise(resolve => setTimeout(resolve, 60));
      }
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Reset
      setAnimationPhase('reset');
      setNewNodeData(null);
      setShowSecondPopup(false);
      setTypedText('');
      setShowUserMessage(false);
      setStreamedText('');
      setTextSelected(false);
      setNodes([
        { id: 'root', title: 'Climate Change', type: 'main' },
        { id: 'branch1', title: 'Renewable Energy', type: 'branch' },
        { id: 'branch2', title: 'Carbon Capture', type: 'branch' },
      ]);
      setLinks([
        { source: 'root', target: 'branch1' },
        { source: 'root', target: 'branch2' },
      ]);
      setCursorPosition({ x: 180, y: 220 });
      setAnimationPhase('init');

      animationRunningRef.current = false;
    };

    // Intersection Observer to trigger animation on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationRunningRef.current) {
            runSequence();
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of component is visible
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      {/* D3 Graph */}
      <svg
        ref={svgRef}
        className="absolute w-full h-full"
        style={{
          zIndex: 1,
          ...(isMobile ? { top: '400px', height: 'calc(100% - 400px)' } : { top: 0 })
        }}
      />

      {/* Chat Popup - Fixed position bottom left on desktop, top left on mobile */}
      <div
        className="absolute rounded-xl shadow-2xl flex flex-col"
        style={{
          left: `${20 * scale}px`,
          ...(isMobile ? { top: `${20 * scale}px` } : { bottom: `${20 * scale}px` }),
          width: `${320 * scale}px`,
          height: `${380 * scale}px`,
          backgroundColor: '#111111',
          border: `${3 * scale}px solid #94d2bd`,
          boxShadow: '0 0 30px rgba(148, 210, 189, 0.3)',
          zIndex: isMobile ? 50 : 30,
        }}
      >
        {/* Header */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">Climate Change Overview</h3>
          <div className="flex items-center gap-2">
            <div className="px-2 py-1 rounded text-xs bg-white/5 text-white/60 border border-white/10">
              GPT-4o
            </div>
            <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <TagIcon className="w-4 h-4 text-white/60" />
            </button>
            <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <ShareIcon className="w-4 h-4 text-white/60" />
            </button>
            <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <XMarkIcon className="w-4 h-4 text-white/60" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* User Message */}
          <div className="flex justify-end">
            <div className="bg-white/10 rounded-lg px-3 py-2 max-w-[80%]">
              <p className="text-white" style={{ fontSize: `${10 * scale}px` }}>{userMessage}</p>
            </div>
          </div>

          {/* AI Response */}
          <div className="flex justify-start">
            <div className="max-w-[85%] relative">
              <div className="text-white/90 leading-relaxed" style={{ fontSize: '10px' }}>
                {aiResponse.split('\n').map((line, i) => (
                  <p
                    key={i}
                    className={`${i === 2 && textSelected ? 'bg-[#94d2bd]/30 rounded px-1' : ''}`}
                    style={{
                      marginBottom: line === '' ? '0.75rem' : '0',
                      ...(i === 2 && textSelected ? {
                        textDecoration: 'underline',
                        textDecorationColor: '#94d2bd',
                        textDecorationThickness: '2px',
                        textUnderlineOffset: '2px'
                      } : {})
                    }}
                  >
                    {renderTextWithBold(line)}
                  </p>
                ))}
              </div>
              <div className="mt-2 text-xs text-white/40">GPT-4o • 2:34 PM</div>

              {/* Ramify Button */}
              <AnimatePresence>
                {showRamifyButton && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="absolute left-0 bottom-12 z-50"
                  >
                    <div className="bg-[#94d2bd] text-black px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-2 text-xs font-medium whitespace-nowrap">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21l3-3-3-3m8 6V9a3 3 0 00-3-3H9" />
                      </svg>
                      Ramify from here
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Input Area */}
        <div className="p-3 border-t border-white/10">
          <div className="flex gap-2 items-end rounded-xl border border-white/20 bg-white/5 px-3 py-2">
            <input
              type="text"
              placeholder="Ask anything..."
              className="flex-1 bg-transparent text-sm text-white placeholder-white/40 outline-none"
              disabled
              style={{ fontSize: `${10 * scale}px` }}
            />
            <button className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Animated Cursor */}
      <AnimatePresence>
        {animationPhase !== 'init' && animationPhase !== 'reset' && (
          <motion.div
            className="absolute pointer-events-none z-50"
            initial={{ x: 0, y: 0 }}
            animate={{
              x: cursorPosition.x,
              y: cursorPosition.y,
              scale: (animationPhase === 'clicking' || animationPhase === 'clickingNode') ? 0.9 : 1
            }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 20,
              scale: { duration: 0.1 }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="black" strokeWidth="1">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Node Glow Effect */}
      {newNodeData && animationPhase === 'branching' && (
        <motion.div
          className="absolute w-4 h-4 rounded-full bg-[#ee9b00]"
          initial={{ scale: 0, opacity: 0 }}
          animate={{
            scale: [0, 2, 0],
            opacity: [0, 0.6, 0]
          }}
          transition={{
            duration: 1.5,
            repeat: 2,
            ease: "easeInOut"
          }}
          style={{
            left: '50%',
            top: '50%',
            filter: 'blur(20px)',
            zIndex: 25
          }}
        />
      )}

      {/* Second Chat Popup - Right side */}
      <AnimatePresence>
        {showSecondPopup && (
          <motion.div
            className="absolute rounded-xl shadow-2xl flex flex-col"
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            style={{
              ...(isMobile ? { right: `${20 * scale}px`, top: '800px' } : { right: `${20 * scale}px`, bottom: `${20 * scale}px` }),
              width: `${320 * scale}px`,
              height: `${380 * scale}px`,
              backgroundColor: '#111111',
              border: `${3 * scale}px solid #005f73`,
              boxShadow: '0 0 30px rgba(0, 95, 115, 0.3)',
              zIndex: isMobile ? 50 : 30,
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Mitigation Strategies</h3>
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 rounded text-xs bg-white/5 text-white/60 border border-white/10">
                  GPT-4o
                </div>
                <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                  <TagIcon className="w-4 h-4 text-white/60" />
                </button>
                <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                  <ShareIcon className="w-4 h-4 text-white/60" />
                </button>
                <button className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                  <XMarkIcon className="w-4 h-4 text-white/60" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* User Message - only show when sent */}
              {showUserMessage && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-end"
                >
                  <div className="bg-white/10 rounded-lg px-3 py-2 max-w-[80%]">
                    <p className="text-white" style={{ fontSize: '10px' }}>Tell me more about mitigation</p>
                  </div>
                </motion.div>
              )}

              {/* AI Response - streaming */}
              {streamedText && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="max-w-[85%]">
                    <div className="text-white/90 leading-relaxed" style={{ fontSize: '10px' }}>
                      {streamedText}
                    </div>
                    {streamedText.length > 100 && (
                      <div className="mt-2 text-xs text-white/40">GPT-4o • 2:35 PM</div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input Area */}
            <div className="p-3 border-t border-white/10">
              <div className="flex gap-2 items-end rounded-xl border border-white/20 bg-white/5 px-3 py-2">
                <div className="flex-1 text-sm text-white" style={{ fontSize: '10px' }}>
                  {typedText || <span className="text-white/40">Ask anything...</span>}
                </div>
                <motion.button
                  animate={{
                    scale: animationPhase === 'sending' ? 0.9 : 1
                  }}
                  className="p-2 rounded-lg bg-orange-500/20 text-orange-400"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
